import React, { useState } from "react";
import { router } from "@inertiajs/react";
import {
  Tag,
  Activity,
  Building2,
  Layers,
  Globe,
  Users,
  FileText,
  Edit2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";

interface Offer {
  id: number;
  name: string;
  client: string;
  status: string;
  vertical: string;
  country: string;
  provider: string;
  description?: string;
}

interface BasicInfoProps {
  offer: Offer;
  is_admin?: boolean;
}

export default function BasicInfo({ offer, is_admin = false }: BasicInfoProps) {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [description, setDescription] = useState(offer.description || "");
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = () => {
    setIsSaving(true);
    router.patch(
      `/offers/${offer.id}`,
      { offer: { description } },
      {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
          toast.success("Description updated successfully");
          setIsEditDialogOpen(false);
        },
        onError: () => {
          toast.error("Failed to update description");
        },
        onFinish: () => {
          setIsSaving(false);
        },
      }
    );
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200">
      <div className="p-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 divide-x divide-gray-200">
          {/* Left Column - Offer Description */}
          <div className="pr-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="flex items-center justify-center w-8 h-8 bg-indigo-100 rounded-lg mr-3">
                  <FileText className="w-4 h-4 text-indigo-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Offer Description
                </h3>
              </div>
              {is_admin && (
                <button
                  onClick={() => setIsEditDialogOpen(true)}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                  title="Edit description"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              )}
            </div>
            <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
              {offer.description || "No description available"}
            </p>
          </div>

          {/* Right Column - Offer Details */}
          <div className="pl-8 space-y-6">
            <div className="space-y-5">
              <div className="py-3 border-b border-gray-200 last:border-b-0">
                <div className="flex items-center mb-2">
                  <Tag className="w-4 h-4 mr-3 text-gray-500" />
                  <span className="text-sm font-medium text-gray-600">
                    Offer Name
                  </span>
                </div>
                <div className="pl-7">
                  <span className="text-sm font-semibold text-gray-900 break-words">
                    {offer.name}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between py-3 border-b border-gray-200 last:border-b-0">
                <div className="flex items-center">
                  <Activity className="w-4 h-4 mr-3 text-gray-500" />
                  <span className="text-sm font-medium text-gray-600">
                    Status
                  </span>
                </div>
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                    offer.status === "active"
                      ? "bg-green-100 text-green-800"
                      : offer.status === "inactive"
                      ? "bg-red-100 text-red-800"
                      : "bg-yellow-100 text-yellow-800"
                  }`}
                >
                  <div
                    className={`w-1.5 h-1.5 rounded-full mr-2 ${
                      offer.status === "active"
                        ? "bg-green-600"
                        : offer.status === "inactive"
                        ? "bg-red-600"
                        : "bg-yellow-600"
                    }`}
                  />
                  {offer.status.charAt(0).toUpperCase() + offer.status.slice(1)}
                </span>
              </div>

              <div className="flex items-center justify-between py-3 border-b border-gray-200 last:border-b-0">
                <div className="flex items-center">
                  <Building2 className="w-4 h-4 mr-3 text-gray-500" />
                  <span className="text-sm font-medium text-gray-600">
                    Client
                  </span>
                </div>
                <span className="text-sm font-semibold text-gray-900">
                  {offer.client}
                </span>
              </div>

              <div className="flex items-center justify-between py-3 border-b border-gray-200 last:border-b-0">
                <div className="flex items-center">
                  <Layers className="w-4 h-4 mr-3 text-gray-500" />
                  <span className="text-sm font-medium text-gray-600">
                    Vertical
                  </span>
                </div>
                <span className="text-sm font-semibold text-gray-900">
                  {offer.vertical}
                </span>
              </div>

              <div className="flex items-center justify-between py-3 border-b border-gray-200 last:border-b-0">
                <div className="flex items-center">
                  <Globe className="w-4 h-4 mr-3 text-gray-500" />
                  <span className="text-sm font-medium text-gray-600">
                    Country
                  </span>
                </div>
                <span className="text-sm font-semibold text-gray-900">
                  {offer.country}
                </span>
              </div>

              <div className="flex items-center justify-between py-3">
                <div className="flex items-center">
                  <Users className="w-4 h-4 mr-3 text-gray-500" />
                  <span className="text-sm font-medium text-gray-600">
                    Provider
                  </span>
                </div>
                <span className="text-sm font-semibold text-gray-900">
                  {offer.provider}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Description Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[625px]">
          <DialogHeader>
            <DialogTitle>Edit Offer Description</DialogTitle>
            <DialogDescription>
              Update the description for {offer.name}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter offer description..."
              className="min-h-[200px] resize-none"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsEditDialogOpen(false);
                setDescription(offer.description || "");
              }}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
